const IDENTITY = {
  completedOn: 'Date completing the form',
  nhsNumber: 'NHS number',
  practiceName: 'GP practice',
  raiserName: 'Name of person raising the alert',
  practicePhone: 'Tel',
  practiceEmail: 'Generic email',
};

function selectedId(value) {
  return value && typeof value === 'object' ? value.id : value || '';
}

function tick(on) {
  return on ? '☑' : '☐';
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function nelPlainText(template, fields, context) {
  const providerId = selectedId(fields.provider);
  const urgencyId = selectedId(fields.urgency);
  const boroughId = selectedId(fields.borough);
  const lines = [
    template.displayName,
    template.icbName,
    '',
    template.intro,
    '',
    'NAME OF ORGANISATION',
    'Please email this form to the relevant address below.',
    '',
  ];
  for (const provider of template.providers || []) {
    const on = provider.id === providerId;
    const other = on && provider.id === 'other' && fields.provider?.other ? ` — ${fields.provider.other}` : '';
    lines.push(`${tick(on)} ${provider.label}${other}`);
    if (provider.email) lines.push(`   ${provider.email}`);
  }
  lines.push('', 'URGENT OR NOT URGENT');
  for (const option of template.urgencyOptions || []) {
    lines.push(`${tick(option.id === urgencyId)} ${option.label}`);
  }
  lines.push('');
  for (const key of template.identityFields || []) {
    if (key === 'practicePhone' || key === 'practiceEmail') continue;
    lines.push(IDENTITY[key] || key);
    lines.push(context?.[key] || '');
    lines.push('');
  }
  lines.push('PRACTICE CONTACT DETAILS');
  lines.push(`Tel: ${context?.practicePhone || ''}`);
  lines.push(`Generic email: ${context?.practiceEmail || ''}`);
  lines.push('', 'WHICH BOROUGH DO YOU WORK IN?');
  for (const borough of template.boroughs || []) {
    lines.push(`${tick(borough.id === boroughId)} ${borough.label}`);
  }
  lines.push(
    '',
    'PLEASE TELL US ABOUT THE QUALITY ISSUE',
    'Include the discipline or specialty, the dates for this episode, your question, and the outcome you want.',
    '',
    fields.narrative || '',
    '',
    template.sla,
    '',
    'TRUST RESPONSE — for Trust use only',
    'Please include learning and action taken:',
    '',
    '',
    'Contact for further information:',
    '',
  );
  return lines.join('\n');
}

export function nelWordHtml(template, fields, context) {
  const providerId = selectedId(fields.provider);
  const urgencyId = selectedId(fields.urgency);
  const boroughId = selectedId(fields.borough);
  const providerRows = (template.providers || []).map((provider) => {
    const on = provider.id === providerId;
    const name = provider.label + (on && provider.id === 'other' && fields.provider?.other ? ` — ${fields.provider.other}` : '');
    return `<tr class="${on ? 'picked' : ''}"><td>${tick(on)} ${escapeHtml(name)}</td><td>${escapeHtml(provider.email || '')}</td></tr>`;
  }).join('');
  const urgency = (template.urgencyOptions || []).map((option) => (
    `<span class="check ${option.id === urgencyId ? 'picked' : ''}">${tick(option.id === urgencyId)} ${escapeHtml(option.label)}</span>`
  )).join('');
  const details = (template.identityFields || [])
    .filter((key) => key !== 'practicePhone' && key !== 'practiceEmail')
    .map((key) => `<tr><th>${escapeHtml(IDENTITY[key] || key)}</th><td>${escapeHtml(context?.[key] || '')}</td></tr>`)
    .join('');
  const boroughs = (template.boroughs || []).map((borough) => (
    `<span class="check ${borough.id === boroughId ? 'picked' : ''}">${tick(borough.id === boroughId)} ${escapeHtml(borough.label)}</span>`
  )).join('');

  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word">
<head><meta charset="UTF-8"><title>${escapeHtml(template.displayName)}</title>
<style>
  body { font-family: Calibri, Arial, sans-serif; color: #1a1a1a; max-width: 760px; margin: 28px auto; }
  h1 { font-size: 20px; text-align: center; margin: 0 0 4px; }
  .icb { text-align: center; color: #444; margin: 0 0 16px; }
  .intro, .sla { font-size: 13px; line-height: 1.45; }
  h2 { font-size: 13px; letter-spacing: 0.04em; text-transform: uppercase; border-bottom: 1px solid #222; padding-bottom: 4px; margin: 22px 0 8px; }
  table { width: 100%; border-collapse: collapse; }
  td, th { border: 1px solid #bbb; padding: 6px 8px; vertical-align: top; font-size: 13px; text-align: left; }
  th { width: 42%; background: #f4f4f4; font-weight: 600; }
  tr.picked td { background: #e7f6ec; font-weight: 700; }
  .check { display: inline-block; min-width: 46%; margin: 3px 0; }
  .check.picked { font-weight: 700; }
  .issue { border: 1px solid #222; min-height: 120px; padding: 10px 12px; white-space: pre-wrap; line-height: 1.45; }
  .blank { border-bottom: 1px solid #222; height: 28px; margin: 8px 0 16px; }
  .hint { color: #444; font-size: 12px; margin: 0 0 8px; }
</style></head>
<body>
  <h1>${escapeHtml(template.displayName)}</h1>
  <p class="icb">${escapeHtml(template.icbName)}</p>
  <p class="intro">${escapeHtml(template.intro)}</p>
  <h2>Name of organisation</h2>
  <p class="hint">Please email this form to the relevant address below.</p>
  <table><tbody>${providerRows}</tbody></table>
  <h2>Urgent or not urgent</h2>
  <p>${urgency}</p>
  <h2>About this alert</h2>
  <table><tbody>${details}</tbody></table>
  <h2>Practice contact details</h2>
  <table><tbody>
    <tr><th>Tel</th><td>${escapeHtml(context?.practicePhone || '')}</td></tr>
    <tr><th>Generic email</th><td>${escapeHtml(context?.practiceEmail || '')}</td></tr>
  </tbody></table>
  <h2>Which borough do you work in?</h2>
  <p>${boroughs}</p>
  <h2>Please tell us about the quality issue</h2>
  <p class="hint">Include the discipline or specialty, the dates for this episode, your question, and the outcome you want.</p>
  <div class="issue">${escapeHtml(fields.narrative || '')}</div>
  <p class="sla">${escapeHtml(template.sla)}</p>
  <h2>Trust response — for Trust use only</h2>
  <p class="hint">Please include learning and action taken.</p>
  <div class="blank"></div>
  <p class="hint">Contact for further information.</p>
  <div class="blank"></div>
</body></html>`;
}
